import { DashboardLayout } from '@/components/layout/DashboardLayout';
import AIAnalystPanel from '@/components/ai/AIAnalystPanel';
import { useNavigate } from 'react-router-dom';
import { useState, useEffect, useCallback } from 'react';
import { useProject } from '@/contexts/ProjectContext';
import { PermissionGate } from '@/components/PermissionGate';
import {
  Shield, ArrowLeft, RefreshCw, Loader2, AlertTriangle, CheckCircle2,
  Clock, ChevronRight, Activity, Eye, ClipboardCheck, FileText,
  Users, Siren, Flame, HardHat, TrendingDown, TrendingUp, Target,
  Calendar, Award, Megaphone, Download, Plus, Upload, Sparkles
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import api from '@/lib/api';
import ModuleHelp from '@/components/common/ModuleHelp';
import ModuleInsightsDashboard from '@/components/common/ModuleInsightsDashboard';
import { SafetyTriangle } from '@/components/hse/SafetyTriangle';
import { IncidentCreateForm, BulkImportPanel } from '@/components/hse/IncidentForm';
import HSECreateModal from '@/components/hse/HSECreateModal';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  LineChart, Line, Legend, PieChart, Pie, Cell, AreaChart, Area
} from 'recharts';

const TABS = [
  { id: 'overview', label: 'Overview', icon: Shield },
  { id: 'insights', label: 'AI Insights', icon: Sparkles },
  { id: 'incidents', label: 'Incidents', icon: AlertTriangle },
  { id: 'permits', label: 'Permits', icon: FileText },
  { id: 'environmental', label: 'Environmental', icon: Activity },
  { id: 'inspections', label: 'Inspections', icon: ClipboardCheck },
  { id: 'observations', label: 'Observations', icon: Eye },
  { id: 'tbt-training', label: 'TBT & Training', icon: Users },
];

const INC_TYPE_COLORS = {
  NEAR_MISS: '#84CC16', FAC: '#FACC15', MTC: '#F59E0B', RWC: '#F97316',
  LTI: '#DC2626', FATALITY: '#991B1B', PROPERTY_DAMAGE: '#143A6B',
  ENVIRONMENTAL: '#06B6D4', VEHICLE_ACCIDENT: '#64748B',
};

const SEVERITY_COLORS = { NEGLIGIBLE: '#94A3B8', MINOR: '#F59E0B', MAJOR: '#EF4444', CRITICAL: '#991B1B' };
const STATUS_COLORS = { REPORTED: '#3B82F6', UNDER_INVESTIGATION: '#143A6B', CAPA_IN_PROGRESS: '#F59E0B', CLOSED: '#22C55E', DRAFT: '#94A3B8', ACTIVE: '#22C55E', AREA_AUTHORITY_APPROVED: '#0EA5E9', HSE_APPROVED: '#6366F1', CLOSED_NORMAL: '#22C55E', EXPIRED: '#94A3B8' };

function KPICard({ label, value, unit, icon: Icon, color, trend, target }) {
  const atTarget = target !== undefined && parseFloat(value) <= target;
  return (
    <div className="rounded-xl p-4 relative overflow-hidden" style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }} data-testid={`kpi-${label.replace(/\s+/g, '-').toLowerCase()}`}>
      <div className="flex items-start justify-between mb-2">
        <div className="p-1.5 rounded-lg" style={{ background: `${color}15`, border: `1px solid ${color}25` }}>
          <Icon className="w-4 h-4" style={{ color }} />
        </div>
        {target !== undefined && (
          <span className="text-[10px] px-1.5 py-0.5 rounded-full font-medium" style={{ backgroundColor: atTarget ? '#22C55E15' : '#EF444415', color: atTarget ? '#22C55E' : '#EF4444' }}>
            {atTarget ? 'On Target' : 'Off Target'}
          </span>
        )}
      </div>
      <div className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>{value}<span className="text-xs font-normal ml-1" style={{ color: 'var(--text-muted)' }}>{unit}</span></div>
      <div className="text-[10px] font-medium mt-1" style={{ color: 'var(--text-muted)' }}>{label}</div>
      {trend !== undefined && (
        <div className="flex items-center gap-1 mt-1">
          {trend >= 0 ? <TrendingUp className="w-3 h-3 text-red-500" /> : <TrendingDown className="w-3 h-3 text-green-500" />}
          <span className="text-[10px]" style={{ color: trend >= 0 ? '#EF4444' : '#22C55E' }}>{Math.abs(trend)}% vs prev</span>
        </div>
      )}
    </div>
  );
}

function StatusBadge({ status }) {
  const color = STATUS_COLORS[status] || '#94A3B8';
  return (
    <span className="text-[10px] px-2 py-0.5 rounded-full font-medium" style={{ backgroundColor: `${color}15`, color, border: `1px solid ${color}30` }}>
      {status?.replace(/_/g, ' ')}
    </span>
  );
}

function DataTable({ columns, data, emptyMessage = 'No data', onRowClick }) {
  if (!data?.length) return <div className="text-center py-12 text-sm" style={{ color: 'var(--text-muted)' }}>{emptyMessage}</div>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr style={{ borderBottom: '1px solid var(--border-color)' }}>
            {columns.map(col => (
              <th key={col.key} className="text-left px-3 py-2.5 text-[10px] font-semibold uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>{col.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.map((row, i) => (
            <tr key={i} onClick={() => onRowClick?.(row)} className={onRowClick ? 'cursor-pointer hover:brightness-95' : ''} style={{ borderBottom: '1px solid var(--border-color)', backgroundColor: i % 2 === 0 ? 'transparent' : 'var(--hover-bg)' }}>
              {columns.map(col => (
                <td key={col.key} className="px-3 py-2.5" style={{ color: 'var(--text-primary)' }}>
                  {col.render ? col.render(row[col.key], row) : (row[col.key] ?? '—')}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function LeadingIndicator({ label, value, total, color }) {
  const pct = total > 0 ? Math.round(value / total * 100) : 0;
  return (
    <div className="flex items-center gap-3">
      <div className="flex-1">
        <div className="flex justify-between mb-1">
          <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>{label}</span>
          <span className="text-xs font-semibold" style={{ color: 'var(--text-primary)' }}>{value}{total ? `/${total}` : ''}</span>
        </div>
        <div className="h-1.5 rounded-full" style={{ backgroundColor: 'var(--border-color)' }}>
          <div className="h-full rounded-full transition-all" style={{ width: `${Math.min(pct, 100)}%`, backgroundColor: color }} />
        </div>
      </div>
    </div>
  );
}

// ── Overview Tab ──
function OverviewTab({ stats, triangle, trend, projectId }) {
  const kpis = stats?.kpis || {};
  const mh = stats?.manhours || {};
  const leading = stats?.leadingIndicators || {};
  const targets = kpis.targets || {};
  const [downloading, setDownloading] = useState(false);

  const handleDownloadPDF = async () => {
    setDownloading(true);
    try {
      const res = await api.get(`/v1/projects/${projectId}/hse/report/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `HSE_Report_${stats?.periodLabel?.replace(/\s+/g, '_') || 'current'}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success('PDF report downloaded');
    } catch {
      toast.error('Failed to generate PDF');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* PDF Download Button */}
      <div className="flex justify-end">
        <Button variant="outline" size="sm" onClick={handleDownloadPDF} disabled={downloading || !stats?.periodId} data-testid="download-pdf-btn">
          {downloading ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Download className="w-3.5 h-3.5 mr-1" />}
          Download PDF Report
        </Button>
      </div>
      {/* KPI Cards Row */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <KPICard label="LTIFR" value={kpis.ltifr ?? '—'} unit="" icon={AlertTriangle} color="#DC2626" target={targets.ltifrTarget} />
        <KPICard label="TRIFR" value={kpis.trifr ?? '—'} unit="" icon={Activity} color="#F97316" target={targets.trifrTarget} />
        <KPICard label="Days Since Last LTI" value={kpis.daysSinceLastLTI ?? '—'} unit="days" icon={Calendar} color="#22C55E" />
        <KPICard label="Cumulative Manhours" value={mh.cumulative ? `${(mh.cumulative / 1000000).toFixed(2)}M` : '—'} unit="" icon={Clock} color="#3B82F6" />
        <KPICard label="Severity Rate" value={kpis.severityRate ?? '—'} unit="" icon={Flame} color="#D97706" target={targets.severityRateTarget} />
        <KPICard label="CAPA Closure" value={kpis.capaClosureRate ? `${kpis.capaClosureRate}%` : '—'} unit="" icon={CheckCircle2} color="#143A6B" />
      </div>

      {/* Safety Triangle + Leading Indicators */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <SafetyTriangle tiers={triangle?.tiers || []} healthScore={triangle?.healthScore || 0} manhours={triangle?.manhours || {}} />
        <div className="rounded-xl p-5" style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }}>
          <h3 className="text-sm font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>Leading Indicators (Current Period)</h3>
          <div className="space-y-3">
            <LeadingIndicator label="Inspections" value={leading.inspectionsConducted || 0} total={leading.inspectionsPlanned || 0} color="#3B82F6" />
            <LeadingIndicator label="TBTs Conducted" value={leading.tbtsConducted || 0} total={leading.tbtsPlanned || 0} color="#143A6B" />
            <LeadingIndicator label="Safety Observations" value={leading.safetyObservations || 0} total={leading.safetyObservations || 0} color="#22C55E" />
            <LeadingIndicator label="CAPA Closed" value={leading.capaClosed || 0} total={leading.capaRaised || 0} color="#F59E0B" />
            <LeadingIndicator label="Permits Issued" value={leading.permitsIssued || 0} total={leading.permitsIssued || 0} color="#0EA5E9" />
            <LeadingIndicator label="Training Hours" value={leading.trainingHours || 0} total={600} color="#EC4899" />
            <LeadingIndicator label="Mgmt Walkdowns" value={leading.managementWalkdowns || 0} total={12} color="#6366F1" />
            <LeadingIndicator label="Emergency Drills" value={leading.emergencyDrills || 0} total={2} color="#EF4444" />
          </div>
        </div>
      </div>

      {/* KPI Trend Charts */}
      {trend?.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <div className="rounded-xl p-5" style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }}>
            <h3 className="text-sm font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>LTIFR & TRIFR Trend</h3>
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={trend}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color)" />
                <XAxis dataKey="period" tick={{ fontSize: 10, fill: 'var(--text-muted)' }} />
                <YAxis tick={{ fontSize: 10, fill: 'var(--text-muted)' }} />
                <Tooltip contentStyle={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)', fontSize: 12 }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Line type="monotone" dataKey="ltifr" name="LTIFR" stroke="#DC2626" strokeWidth={2} dot={{ r: 3 }} />
                <Line type="monotone" dataKey="trifr" name="TRIFR" stroke="#F97316" strokeWidth={2} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
          <div className="rounded-xl p-5" style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }}>
            <h3 className="text-sm font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>Monthly Incidents</h3>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={trend}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color)" />
                <XAxis dataKey="period" tick={{ fontSize: 10, fill: 'var(--text-muted)' }} />
                <YAxis tick={{ fontSize: 10, fill: 'var(--text-muted)' }} />
                <Tooltip contentStyle={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)', fontSize: 12 }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="nearMiss" name="Near Miss" fill="#84CC16" radius={[2, 2, 0, 0]} />
                <Bar dataKey="fac" name="FAC" fill="#FACC15" radius={[2, 2, 0, 0]} />
                <Bar dataKey="recordable" name="Recordable" fill="#DC2626" radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* By Contractor */}
      {stats?.byContractor?.length > 0 && (
        <div className="rounded-xl p-5" style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }}>
          <h3 className="text-sm font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>Performance by Contractor</h3>
          <DataTable columns={[
            { key: 'contractorName', label: 'Contractor' },
            { key: 'contractorType', label: 'Type', render: v => <span className="text-xs">{v?.replace(/_/g, ' ')}</span> },
            { key: 'manhours', label: 'Manhours', render: v => v?.toLocaleString() },
            { key: 'incidents', label: 'Incidents' },
            { key: 'nearMiss', label: 'Near Miss' },
            { key: 'trifr', label: 'TRIFR', render: v => <span style={{ color: v > 0.5 ? '#EF4444' : '#22C55E' }}>{v}</span> },
          ]} data={stats.byContractor} />
        </div>
      )}
    </div>
  );
}

// ── Incidents Tab ──
function IncidentsTab({ incidents, total, projectId, onRefresh }) {
  const [typeFilter, setTypeFilter] = useState('');
  const [severityFilter, setSeverityFilter] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showImport, setShowImport] = useState(false);

  const filtered = incidents.filter(i =>
    (!typeFilter || i.incidentType === typeFilter) &&
    (!severityFilter || i.incidentSeverity === severityFilter)
  );

  const types = [...new Set(incidents.map(i => i.incidentType))].sort();
  const severities = [...new Set(incidents.map(i => i.incidentSeverity))].sort();

  const typeSummary = {};
  incidents.forEach(i => { typeSummary[i.incidentType] = (typeSummary[i.incidentType] || 0) + 1; });

  return (
    <div className="space-y-4">
      {/* Action buttons */}
      <div className="flex items-center gap-2 flex-wrap">
        <PermissionGate module="HSE" action="create">
          <Button size="sm" onClick={() => setShowCreateForm(true)} data-testid="create-incident-btn">
            <Plus className="w-3.5 h-3.5 mr-1" /> Report Incident
          </Button>
        </PermissionGate>
        <PermissionGate module="HSE" action="import">
          <Button variant="outline" size="sm" onClick={() => setShowImport(!showImport)} data-testid="toggle-import-btn">
            <Upload className="w-3.5 h-3.5 mr-1" /> Bulk Import
          </Button>
        </PermissionGate>
      </div>

      {/* Bulk Import Panel */}
      {showImport && <BulkImportPanel projectId={projectId} type="incidents" onImported={onRefresh} />}

      {/* Create Form Modal */}
      {showCreateForm && (
        <IncidentCreateForm projectId={projectId} onCreated={() => { setShowCreateForm(false); onRefresh?.(); }} onClose={() => setShowCreateForm(false)} />
      )}

      {/* Type summary chips */}
      <div className="flex flex-wrap gap-2">
        {Object.entries(typeSummary).sort((a, b) => b[1] - a[1]).map(([t, c]) => (
          <button key={t} onClick={() => setTypeFilter(typeFilter === t ? '' : t)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all"
            style={{ backgroundColor: typeFilter === t ? INC_TYPE_COLORS[t] : `${INC_TYPE_COLORS[t] || '#94A3B8'}15`, color: typeFilter === t ? 'white' : INC_TYPE_COLORS[t] || '#94A3B8', border: `1px solid ${INC_TYPE_COLORS[t] || '#94A3B8'}30` }}
            data-testid={`filter-${t}`}>
            {t.replace(/_/g, ' ')} <span className="font-bold">{c}</span>
          </button>
        ))}
      </div>

      {/* Severity filter */}
      <div className="flex items-center gap-2">
        <span className="text-xs" style={{ color: 'var(--text-muted)' }}>Severity:</span>
        {severities.map(s => (
          <button key={s} onClick={() => setSeverityFilter(severityFilter === s ? '' : s)}
            className="text-[10px] px-2 py-1 rounded-md font-medium"
            style={{ backgroundColor: severityFilter === s ? SEVERITY_COLORS[s] : `${SEVERITY_COLORS[s]}15`, color: severityFilter === s ? 'white' : SEVERITY_COLORS[s] }}>
            {s}
          </button>
        ))}
        <span className="ml-auto text-xs" style={{ color: 'var(--text-disabled)' }}>Showing {filtered.length} of {total}</span>
      </div>

      {/* Incidents table */}
      <div className="rounded-xl overflow-hidden" style={{ border: '1px solid var(--border-color)' }}>
        <DataTable columns={[
          { key: 'incidentNumber', label: 'ID', render: v => <span className="font-mono text-xs font-semibold" style={{ color: 'var(--accent-text)' }}>{v}</span> },
          { key: 'incidentDate', label: 'Date', render: v => v ? new Date(v).toLocaleDateString() : '—' },
          { key: 'incidentType', label: 'Type', render: v => <span className="text-xs font-medium px-2 py-0.5 rounded" style={{ backgroundColor: `${INC_TYPE_COLORS[v] || '#94A3B8'}15`, color: INC_TYPE_COLORS[v] || '#94A3B8' }}>{v?.replace(/_/g, ' ')}</span> },
          { key: 'incidentSeverity', label: 'Severity', render: v => <span className="text-xs font-medium" style={{ color: SEVERITY_COLORS[v] || '#94A3B8' }}>{v}</span> },
          { key: 'area', label: 'Area' },
          { key: 'incidentCategory', label: 'Category', render: v => <span className="text-xs">{v?.replace(/_/g, ' ')}</span> },
          { key: 'status', label: 'Status', render: v => <StatusBadge status={v} /> },
          { key: 'isRecordable', label: 'Rec.', render: v => v ? <CheckCircle2 className="w-3.5 h-3.5 text-red-500" /> : <span className="text-xs" style={{ color: 'var(--text-disabled)' }}>No</span> },
        ]} data={filtered} onRowClick={(row) => setExpandedId(expandedId === row.incidentNumber ? null : row.incidentNumber)} />
      </div>

      {/* Expanded detail */}
      {expandedId && (() => {
        const inc = incidents.find(i => i.incidentNumber === expandedId);
        if (!inc) return null;
        return (
          <div className="rounded-xl p-5 space-y-4" style={{ backgroundColor: 'var(--card-bg)', border: '2px solid var(--accent-text)' }} data-testid="incident-detail">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-semibold" style={{ color: 'var(--text-primary)' }}>{inc.incidentNumber} — {inc.incidentType?.replace(/_/g, ' ')}</h3>
              <button onClick={() => setExpandedId(null)} className="text-xs" style={{ color: 'var(--text-muted)' }}>Close</button>
            </div>
            <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{inc.briefDescription}</p>
            {inc.detailedDescription && <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{inc.detailedDescription}</p>}

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
              <div><span style={{ color: 'var(--text-muted)' }}>Area:</span> <span className="font-medium" style={{ color: 'var(--text-primary)' }}>{inc.area}</span></div>
              <div><span style={{ color: 'var(--text-muted)' }}>Activity:</span> <span className="font-medium" style={{ color: 'var(--text-primary)' }}>{inc.activityBeingPerformed}</span></div>
              <div><span style={{ color: 'var(--text-muted)' }}>Time:</span> <span className="font-medium" style={{ color: 'var(--text-primary)' }}>{inc.incidentTime}</span></div>
              <div><span style={{ color: 'var(--text-muted)' }}>Immediate Action:</span> <span className="font-medium" style={{ color: 'var(--text-primary)' }}>{inc.immediateAction}</span></div>
            </div>

            {/* Injured Persons */}
            {inc.injuredPersons?.length > 0 && (
              <div>
                <h4 className="text-xs font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>Injured Persons</h4>
                {inc.injuredPersons.map((p, j) => (
                  <div key={j} className="flex flex-wrap gap-4 text-xs p-2 rounded-lg mb-1" style={{ backgroundColor: 'var(--hover-bg)' }}>
                    <span><strong>{p.personName}</strong> ({p.trade})</span>
                    <span>Injury: {p.injuryType} — {p.bodyPart}</span>
                    <span>Treatment: {p.treatmentProvided?.replace(/_/g, ' ')}</span>
                    {p.lostWorkdays > 0 && <span className="text-red-500">Lost days: {p.lostWorkdays}</span>}
                    {p.restrictedWorkdays > 0 && <span className="text-orange-500">Restricted days: {p.restrictedWorkdays}</span>}
                  </div>
                ))}
              </div>
            )}

            {/* RCA */}
            {inc.rca?.rcaMethod && (
              <div>
                <h4 className="text-xs font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>Root Cause Analysis ({inc.rca.rcaMethod?.replace(/_/g, ' ')})</h4>
                <div className="text-xs space-y-1 p-3 rounded-lg" style={{ backgroundColor: 'var(--hover-bg)' }}>
                  <div style={{ color: 'var(--text-muted)' }}>Category: <span className="font-medium" style={{ color: 'var(--text-primary)' }}>{inc.rca.rootCauseCategory?.replace(/_/g, ' ')}</span></div>
                  {inc.rca.fiveWhy && Object.entries(inc.rca.fiveWhy).filter(([k]) => k.startsWith('why')).map(([k, v]) => (
                    <div key={k} style={{ color: 'var(--text-secondary)' }}><strong className="text-red-400">{k.toUpperCase()}:</strong> {v}</div>
                  ))}
                  {inc.rca.fiveWhy?.rootCause && <div className="mt-2 font-semibold" style={{ color: '#DC2626' }}>Root Cause: {inc.rca.fiveWhy.rootCause}</div>}
                </div>
              </div>
            )}

            {/* CAPA Items */}
            {inc.capaItems?.length > 0 && (
              <div>
                <h4 className="text-xs font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>CAPA Items ({inc.capaItems.length})</h4>
                <DataTable columns={[
                  { key: 'capaId', label: 'CAPA ID', render: v => <span className="font-mono text-[10px]">{v}</span> },
                  { key: 'capaType', label: 'Type' },
                  { key: 'description', label: 'Description' },
                  { key: 'responsiblePerson', label: 'Responsible' },
                  { key: 'targetDate', label: 'Target', render: v => v ? new Date(v).toLocaleDateString() : '—' },
                  { key: 'status', label: 'Status', render: v => <StatusBadge status={v} /> },
                ]} data={inc.capaItems} />
              </div>
            )}

            {/* Cost */}
            {inc.incidentCost?.totalCost > 0 && (
              <div className="flex gap-4 text-xs">
                <span style={{ color: 'var(--text-muted)' }}>Medical: <strong>{inc.incidentCost.medicalCost?.toLocaleString()}</strong></span>
                <span style={{ color: 'var(--text-muted)' }}>Property: <strong>{inc.incidentCost.propertyCost?.toLocaleString()}</strong></span>
                <span style={{ color: 'var(--text-muted)' }}>Production Loss: <strong>{inc.incidentCost.productionLossCost?.toLocaleString()}</strong></span>
                <span className="font-semibold" style={{ color: '#EF4444' }}>Total: {inc.incidentCost.totalCost?.toLocaleString()}</span>
              </div>
            )}
          </div>
        );
      })()}
    </div>
  );
}

// ── Permits Tab ──
function PermitsTab({ permits, projectId, onRefresh }) {
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showCreate, setShowCreate] = useState(false);

  const types = [...new Set(permits.map(p => p.permitType))].sort();
  const statuses = [...new Set(permits.map(p => p.workflow?.status))].sort();

  const filtered = permits.filter(p =>
    (!typeFilter || p.permitType === typeFilter) &&
    (!statusFilter || p.workflow?.status === statusFilter)
  );

  const statusSummary = {};
  permits.forEach(p => { const s = p.workflow?.status; statusSummary[s] = (statusSummary[s] || 0) + 1; });

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <PermissionGate module="HSE" action="create">
          <Button size="sm" onClick={() => setShowCreate(true)} data-testid="create-permit-btn">
            <Plus className="w-3.5 h-3.5 mr-1" /> New Permit
          </Button>
        </PermissionGate>
      </div>
      {showCreate && (
        <HSECreateModal kind="permit" projectId={projectId}
          onCreated={onRefresh} onClose={() => setShowCreate(false)} />
      )}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {Object.entries(statusSummary).map(([s, c]) => (
          <button key={s} onClick={() => setStatusFilter(statusFilter === s ? '' : s)}
            className="rounded-xl p-3 text-left transition-all"
            style={{ backgroundColor: statusFilter === s ? `${STATUS_COLORS[s]}20` : 'var(--card-bg)', border: `1px solid ${statusFilter === s ? STATUS_COLORS[s] : 'var(--border-color)'}` }}>
            <div className="text-lg font-bold" style={{ color: STATUS_COLORS[s] || 'var(--text-primary)' }}>{c}</div>
            <div className="text-[10px]" style={{ color: 'var(--text-muted)' }}>{s?.replace(/_/g, ' ')}</div>
          </button>
        ))}
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-xs" style={{ color: 'var(--text-muted)' }}>Type:</span>
        {types.map(t => (
          <button key={t} onClick={() => setTypeFilter(typeFilter === t ? '' : t)}
            className="text-[10px] px-2 py-1 rounded-md font-medium transition-all"
            style={{ backgroundColor: typeFilter === t ? '#3B82F6' : 'var(--hover-bg)', color: typeFilter === t ? 'white' : 'var(--text-secondary)' }}>
            {t?.replace(/_/g, ' ')}
          </button>
        ))}
      </div>

      <div className="rounded-xl overflow-hidden" style={{ border: '1px solid var(--border-color)' }}>
        <DataTable columns={[
          { key: 'permitNumber', label: 'Permit #', render: v => <span className="font-mono text-xs font-semibold" style={{ color: 'var(--accent-text)' }}>{v}</span> },
          { key: 'permitType', label: 'Type', render: v => <span className="text-xs">{v?.replace(/_/g, ' ')}</span> },
          { key: 'workDescription', label: 'Description' },
          { key: 'area', label: 'Area' },
          { key: 'contractorName', label: 'Contractor' },
          { key: 'validFrom', label: 'Valid From', render: v => v ? new Date(v).toLocaleDateString() : '—' },
          { key: 'validTo', label: 'Valid To', render: v => v ? new Date(v).toLocaleDateString() : '—' },
          { key: 'workflow', label: 'Status', render: v => <StatusBadge status={v?.status} /> },
        ]} data={filtered} />
      </div>
    </div>
  );
}

// ── Environmental Compliance Tab ──
function EnvironmentalTab({ envData, envDashboard, projectId, onRefresh }) {
  const d = envDashboard || {};
  const [showCreate, setShowCreate] = useState(false);
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <PermissionGate module="HSE" action="create">
          <Button size="sm" onClick={() => setShowCreate(true)} data-testid="create-environmental-btn">
            <Plus className="w-3.5 h-3.5 mr-1" /> New Compliance Item
          </Button>
        </PermissionGate>
      </div>
      {showCreate && (
        <HSECreateModal kind="environmental" projectId={projectId}
          onCreated={onRefresh} onClose={() => setShowCreate(false)} />
      )}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="rounded-xl p-4" style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }}>
          <div className="text-2xl font-bold" style={{ color: '#22C55E' }}>{d.compliant || 0}</div>
          <div className="text-[10px]" style={{ color: 'var(--text-muted)' }}>Compliant</div>
        </div>
        <div className="rounded-xl p-4" style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }}>
          <div className="text-2xl font-bold" style={{ color: '#EF4444' }}>{d.overdue || 0}</div>
          <div className="text-[10px]" style={{ color: 'var(--text-muted)' }}>Overdue</div>
        </div>
        <div className="rounded-xl p-4" style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }}>
          <div className="text-2xl font-bold" style={{ color: '#F59E0B' }}>{d.atRisk || 0}</div>
          <div className="text-[10px]" style={{ color: 'var(--text-muted)' }}>At Risk</div>
        </div>
        <div className="rounded-xl p-4" style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }}>
          <div className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>{d.complianceScore || 0}%</div>
          <div className="text-[10px]" style={{ color: 'var(--text-muted)' }}>Compliance Score</div>
        </div>
      </div>

      {d.byCategory && Object.keys(d.byCategory).length > 0 && (
        <div className="rounded-xl p-5" style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }}>
          <h3 className="text-sm font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>By Category</h3>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={Object.entries(d.byCategory).map(([k, v]) => ({ category: k.replace(/_/g, ' '), ...v }))}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color)" />
              <XAxis dataKey="category" tick={{ fontSize: 9, fill: 'var(--text-muted)' }} angle={-20} textAnchor="end" height={60} />
              <YAxis tick={{ fontSize: 10, fill: 'var(--text-muted)' }} />
              <Tooltip contentStyle={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)', fontSize: 11 }} />
              <Bar dataKey="compliant" name="Compliant" fill="#22C55E" radius={[2, 2, 0, 0]} />
              <Bar dataKey="overdue" name="Overdue" fill="#EF4444" radius={[2, 2, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      <div className="rounded-xl overflow-hidden" style={{ border: '1px solid var(--border-color)' }}>
        <DataTable columns={[
          { key: 'complianceCode', label: 'Code', render: v => <span className="font-mono text-xs">{v}</span> },
          { key: 'complianceName', label: 'Name' },
          { key: 'complianceCategory', label: 'Category', render: v => <span className="text-xs">{v?.replace(/_/g, ' ')}</span> },
          { key: 'frequency', label: 'Frequency' },
          { key: 'nextDueDate', label: 'Next Due', render: v => v ? new Date(v).toLocaleDateString() : '—' },
          { key: 'currentStatus', label: 'Status', render: v => <StatusBadge status={v} /> },
          { key: 'responsiblePerson', label: 'Responsible' },
        ]} data={envData} />
      </div>
    </div>
  );
}

// ── Inspections Tab ──
function InspectionsTab({ inspections, projectId, onRefresh }) {
  const [showCreate, setShowCreate] = useState(false);
  const scoreSummary = { excellent: 0, good: 0, acceptable: 0, poor: 0 };
  inspections.forEach(i => {
    const r = i.overallRating?.toLowerCase();
    if (scoreSummary[r] !== undefined) scoreSummary[r]++;
  });
  const ratingColors = { excellent: '#22C55E', good: '#3B82F6', acceptable: '#F59E0B', poor: '#EF4444' };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <PermissionGate module="HSE" action="create">
          <Button size="sm" onClick={() => setShowCreate(true)} data-testid="create-inspection-btn">
            <Plus className="w-3.5 h-3.5 mr-1" /> New Inspection
          </Button>
        </PermissionGate>
      </div>
      {showCreate && (
        <HSECreateModal kind="inspection" projectId={projectId}
          onCreated={onRefresh} onClose={() => setShowCreate(false)} />
      )}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {Object.entries(scoreSummary).map(([k, v]) => (
          <div key={k} className="rounded-xl p-3" style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }}>
            <div className="text-xl font-bold" style={{ color: ratingColors[k] }}>{v}</div>
            <div className="text-[10px] uppercase" style={{ color: 'var(--text-muted)' }}>{k}</div>
          </div>
        ))}
      </div>
      <div className="rounded-xl overflow-hidden" style={{ border: '1px solid var(--border-color)' }}>
        <DataTable columns={[
          { key: 'inspectionNumber', label: 'ID', render: v => <span className="font-mono text-xs">{v}</span> },
          { key: 'inspectionType', label: 'Type', render: v => <span className="text-xs">{v?.replace(/_/g, ' ')}</span> },
          { key: 'inspectionDate', label: 'Date', render: v => v ? new Date(v).toLocaleDateString() : '—' },
          { key: 'area', label: 'Area' },
          { key: 'inspectedByName', label: 'Inspector' },
          { key: 'complianceScore', label: 'Score', render: v => <span className="font-semibold" style={{ color: v >= 90 ? '#22C55E' : v >= 70 ? '#F59E0B' : '#EF4444' }}>{v}%</span> },
          { key: 'overallRating', label: 'Rating', render: v => <span className="text-xs font-medium" style={{ color: ratingColors[v?.toLowerCase()] }}>{v}</span> },
          { key: 'nonConformingItems', label: 'NCs', render: v => v > 0 ? <span className="text-red-500 font-semibold">{v}</span> : <span style={{ color: 'var(--text-disabled)' }}>0</span> },
        ]} data={inspections} />
      </div>
    </div>
  );
}

// ── Observations Tab ──
function ObservationsTab({ observations, summary, projectId, onRefresh }) {
  const OBS_COLORS = { SAFE_ACT: '#22C55E', UNSAFE_ACT: '#EF4444', UNSAFE_CONDITION: '#F59E0B', SAFE_CONDITION: '#3B82F6', POSITIVE_REINFORCEMENT: '#143A6B', SAFETY_CONVERSATION: '#0EA5E9' };
  const byType = summary?.byType || {};
  const [showImport, setShowImport] = useState(false);
  const [showCreate, setShowCreate] = useState(false);

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <PermissionGate module="HSE" action="create">
          <Button size="sm" onClick={() => setShowCreate(true)} data-testid="create-observation-btn">
            <Plus className="w-3.5 h-3.5 mr-1" /> New Observation
          </Button>
        </PermissionGate>
        <Button variant="outline" size="sm" onClick={() => setShowImport(!showImport)} data-testid="toggle-obs-import-btn">
          <Upload className="w-3.5 h-3.5 mr-1" /> Bulk Import
        </Button>
      </div>
      {showCreate && (
        <HSECreateModal kind="observation" projectId={projectId}
          onCreated={onRefresh} onClose={() => setShowCreate(false)} />
      )}
      {showImport && <BulkImportPanel projectId={projectId} type="observations" onImported={onRefresh} />}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {Object.entries(byType).map(([t, c]) => (
          <div key={t} className="rounded-xl p-3" style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }}>
            <div className="text-lg font-bold" style={{ color: OBS_COLORS[t] || 'var(--text-primary)' }}>{c}</div>
            <div className="text-[10px]" style={{ color: 'var(--text-muted)' }}>{t?.replace(/_/g, ' ')}</div>
          </div>
        ))}
      </div>
      {summary?.safeActPct !== undefined && (
        <div className="rounded-xl p-3 flex items-center gap-3" style={{ backgroundColor: '#22C55E10', border: '1px solid #22C55E25' }}>
          <Award className="w-5 h-5 text-green-500" />
          <span className="text-sm" style={{ color: 'var(--text-primary)' }}>Safe Act Rate: <strong className="text-green-500">{summary.safeActPct}%</strong> of all observations</span>
        </div>
      )}
      <div className="rounded-xl overflow-hidden" style={{ border: '1px solid var(--border-color)' }}>
        <DataTable columns={[
          { key: 'observationNumber', label: 'ID', render: v => <span className="font-mono text-xs">{v}</span> },
          { key: 'observationDate', label: 'Date', render: v => v ? new Date(v).toLocaleDateString() : '—' },
          { key: 'observationType', label: 'Type', render: v => <span className="text-xs font-medium" style={{ color: OBS_COLORS[v] || '#94A3B8' }}>{v?.replace(/_/g, ' ')}</span> },
          { key: 'area', label: 'Area' },
          { key: 'activityObserved', label: 'Activity' },
          { key: 'contractorName', label: 'Contractor' },
          { key: 'observedByName', label: 'Observer' },
        ]} data={observations.slice(0, 100)} />
      </div>
      {observations.length > 100 && <p className="text-xs text-center" style={{ color: 'var(--text-disabled)' }}>Showing 100 of {observations.length} observations</p>}
    </div>
  );
}

// ── TBT & Training Tab ──
function TBTTrainingTab({ tbts, trainings, drills, projectId, onRefresh }) {
  const [subTab, setSubTab] = useState('tbt');
  const [createKind, setCreateKind] = useState(null); // 'tbt' | 'training' | 'drill'

  const KIND_LABELS = { tbt: 'Toolbox Talk', training: 'Training', drill: 'Emergency Drill' };
  const subKindMap = { tbt: 'tbt', training: 'training', drills: 'drill' };
  const activeKind = subKindMap[subTab];

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 flex-wrap">
        {[{ id: 'tbt', label: 'Toolbox Talks', count: tbts.length }, { id: 'training', label: 'Training', count: trainings.length }, { id: 'drills', label: 'Emergency Drills', count: drills.length }].map(t => (
          <button key={t.id} onClick={() => setSubTab(t.id)}
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-medium transition-all"
            style={{ backgroundColor: subTab === t.id ? 'var(--accent-bg)' : 'transparent', color: subTab === t.id ? 'var(--accent-text)' : 'var(--text-muted)', border: `1px solid ${subTab === t.id ? 'var(--accent-text)' : 'var(--border-color)'}` }}
            data-testid={`subtab-${t.id}`}>
            {t.label} <span className="font-bold">{t.count}</span>
          </button>
        ))}
        <div className="ml-auto">
          <PermissionGate module="HSE" action="create">
            <Button size="sm" onClick={() => setCreateKind(activeKind)} data-testid={`create-${activeKind}-btn`}>
              <Plus className="w-3.5 h-3.5 mr-1" /> New {KIND_LABELS[activeKind]}
            </Button>
          </PermissionGate>
        </div>
      </div>

      {createKind && (
        <HSECreateModal kind={createKind} projectId={projectId}
          onCreated={onRefresh} onClose={() => setCreateKind(null)} />
      )}

      {subTab === 'tbt' && (
        <div className="rounded-xl overflow-hidden" style={{ border: '1px solid var(--border-color)' }}>
          <DataTable columns={[
            { key: 'tbtNumber', label: 'TBT #', render: v => <span className="font-mono text-xs">{v}</span> },
            { key: 'tbtDate', label: 'Date', render: v => v ? new Date(v).toLocaleDateString() : '—' },
            { key: 'topic', label: 'Topic' },
            { key: 'topicCategory', label: 'Category', render: v => <span className="text-xs">{v?.replace(/_/g, ' ')}</span> },
            { key: 'conductedByName', label: 'Conducted By' },
            { key: 'area', label: 'Area' },
            { key: 'contractorName', label: 'Contractor' },
            { key: 'attendeeCount', label: 'Attendees', render: v => <span className="font-semibold">{v}</span> },
            { key: 'duration', label: 'Duration', render: v => `${v} min` },
          ]} data={tbts} />
        </div>
      )}

      {subTab === 'training' && (
        <div className="rounded-xl overflow-hidden" style={{ border: '1px solid var(--border-color)' }}>
          <DataTable columns={[
            { key: 'trainingId', label: 'ID', render: v => <span className="font-mono text-xs">{v}</span> },
            { key: 'trainingDate', label: 'Date', render: v => v ? new Date(v).toLocaleDateString() : '—' },
            { key: 'trainingTitle', label: 'Title' },
            { key: 'trainingType', label: 'Type', render: v => <span className="text-xs">{v?.replace(/_/g, ' ')}</span> },
            { key: 'trainingCategory', label: 'Category' },
            { key: 'trainerName', label: 'Trainer' },
            { key: 'attendeeCount', label: 'Attendees', render: v => <span className="font-semibold">{v}</span> },
            { key: 'duration', label: 'Hours', render: v => `${v}h` },
          ]} data={trainings} />
        </div>
      )}

      {subTab === 'drills' && (
        <div className="rounded-xl overflow-hidden" style={{ border: '1px solid var(--border-color)' }}>
          <DataTable columns={[
            { key: 'drillNumber', label: 'Drill #', render: v => <span className="font-mono text-xs">{v}</span> },
            { key: 'drillDate', label: 'Date', render: v => v ? new Date(v).toLocaleDateString() : '—' },
            { key: 'drillType', label: 'Type', render: v => <span className="text-xs">{v?.replace(/_/g, ' ')}</span> },
            { key: 'scenarioLocation', label: 'Location' },
            { key: 'participantCount', label: 'Participants', render: v => <span className="font-semibold">{v ?? '—'}</span> },
            { key: 'responseTime', label: 'Response Time', render: (v, row) => v == null ? '—' : <span style={{ color: v <= row.targetResponseTime ? '#22C55E' : '#EF4444' }}>{v} min</span> },
            { key: 'targetResponseTime', label: 'Target', render: v => v == null ? '—' : `${v} min` },
            { key: 'performanceRating', label: 'Rating', render: v => {
              const c = v === 'EXCELLENT' ? '#22C55E' : v === 'GOOD' ? '#3B82F6' : '#F59E0B';
              return <span className="text-xs font-medium" style={{ color: c }}>{v?.replace(/_/g, ' ')}</span>;
            }},
          ]} data={drills} />
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════
//  MAIN HSE DASHBOARD PAGE
// ═══════════════════════════════════════════
export default function HSEDashboard() {
  const navigate = useNavigate();
  const { selectedProject } = useProject();
  const projectId = selectedProject?.project_id;

  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(false);
  const [seeding, setSeeding] = useState(false);

  // Data states
  const [stats, setStats] = useState(null);
  const [triangle, setTriangle] = useState(null);
  const [trend, setTrend] = useState([]);
  const [incidents, setIncidents] = useState([]);
  const [incidentsTotal, setIncidentsTotal] = useState(0);
  const [permits, setPermits] = useState([]);
  const [envData, setEnvData] = useState([]);
  const [envDashboard, setEnvDashboard] = useState(null);
  const [inspections, setInspections] = useState([]);
  const [observations, setObservations] = useState([]);
  const [obsSummary, setObsSummary] = useState(null);
  const [tbts, setTbts] = useState([]);
  const [trainings, setTrainings] = useState([]);
  const [drills, setDrills] = useState([]);

  const fetchAll = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const base = `/v1/projects/${projectId}/hse`;
      const [statsRes, triRes, trendRes, incRes, permRes, envRes, envDashRes, inspRes, obsRes, obsSumRes, tbtRes, trnRes, drillRes] = await Promise.all([
        api.get(`${base}/statistics/current`).catch(() => ({ data: {} })),
        api.get(`${base}/statistics/safety-triangle`).catch(() => ({ data: {} })),
        api.get(`${base}/statistics/kpi-trend`).catch(() => ({ data: { trend: [] } })),
        api.get(`${base}/incidents`).catch(() => ({ data: { incidents: [], total: 0 } })),
        api.get(`${base}/permits`).catch(() => ({ data: { permits: [] } })),
        api.get(`${base}/environmental/compliance`).catch(() => ({ data: { items: [] } })),
        api.get(`${base}/environmental/compliance/dashboard`).catch(() => ({ data: {} })),
        api.get(`${base}/inspections`).catch(() => ({ data: { inspections: [] } })),
        api.get(`${base}/observations`).catch(() => ({ data: { observations: [] } })),
        api.get(`${base}/observations/summary`).catch(() => ({ data: {} })),
        api.get(`${base}/tbt`).catch(() => ({ data: { tbts: [] } })),
        api.get(`${base}/training`).catch(() => ({ data: { trainings: [] } })),
        api.get(`${base}/emergency/drills`).catch(() => ({ data: { drills: [] } })),
      ]);

      setStats(statsRes.data);
      setTriangle(triRes.data);
      setTrend(trendRes.data?.trend || []);
      setIncidents(incRes.data?.incidents || []);
      setIncidentsTotal(incRes.data?.total || 0);
      setPermits(permRes.data?.permits || []);
      setEnvData(envRes.data?.items || []);
      setEnvDashboard(envDashRes.data);
      setInspections(inspRes.data?.inspections || []);
      setObservations(obsRes.data?.observations || []);
      setObsSummary(obsSumRes.data);
      setTbts(tbtRes.data?.tbts || []);
      setTrainings(trnRes.data?.trainings || []);
      setDrills(drillRes.data?.drills || []);
    } catch (e) {
      toast.error('Failed to load HSE data');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleSeed = async () => {
    if (!projectId) return;
    setSeeding(true);
    try {
      const res = await api.post(`/v1/projects/${projectId}/hse/seed`);
      if (res.data?.seeded) {
        toast.success(`HSE data seeded: ${res.data.incidents} incidents, ${res.data.permits} permits, ${res.data.observations} observations`);
        fetchAll();
      } else {
        toast.info(res.data?.message || 'Data already exists');
      }
    } catch (e) {
      toast.error('Failed to seed HSE data');
    } finally {
      setSeeding(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-5" data-testid="hse-dashboard">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate('/project-controls')} data-testid="hse-back-btn">
              <ArrowLeft className="w-4 h-4 mr-1" /> Back
            </Button>
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-xl" style={{ background: 'linear-gradient(135deg, #DC262620, #F9731620)', border: '1px solid #DC262630' }}>
                <Shield className="w-6 h-6 text-red-500" />
              </div>
              <div>
                <h1 className="text-xl font-bold" style={{ color: 'var(--text-primary)' }}>HSE Management</h1>
                <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Health, Safety & Environment — {selectedProject?.name || 'Select a project'}</p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <ModuleHelp moduleKey="hse" />
            <Button variant="outline" size="sm" onClick={handleSeed} disabled={seeding || !projectId} data-testid="hse-seed-btn">
              {seeding ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <HardHat className="w-3.5 h-3.5 mr-1" />}
              Seed Demo Data
            </Button>
            <Button variant="outline" size="sm" onClick={fetchAll} disabled={loading} data-testid="hse-refresh-btn">
              <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </Button>
          </div>
        </div>

        {/* AI Analyst Panel */}
        {projectId && (
          <AIAnalystPanel projectId={projectId} moduleKey="HSE_MGMT" defaultOpen={false} compact={true} />
        )}

        {/* Tab bar */}
        <div className="flex gap-1 overflow-x-auto pb-1" style={{ borderBottom: '1px solid var(--border-color)' }}>
          {TABS.map(tab => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                className="flex items-center gap-1.5 px-4 py-2.5 text-xs font-medium whitespace-nowrap transition-all rounded-t-lg"
                style={{ color: isActive ? 'var(--accent-text)' : 'var(--text-muted)', backgroundColor: isActive ? 'var(--accent-bg)' : 'transparent', borderBottom: isActive ? '2px solid var(--accent-text)' : '2px solid transparent' }}
                data-testid={`hse-tab-${tab.id}`}>
                <Icon className="w-3.5 h-3.5" />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Loading */}
        {loading && (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin" style={{ color: 'var(--accent-text)' }} />
          </div>
        )}

        {/* No project selected */}
        {!projectId && !loading && (
          <div className="text-center py-20">
            <Shield className="w-12 h-12 mx-auto mb-4" style={{ color: 'var(--text-disabled)' }} />
            <p className="text-sm" style={{ color: 'var(--text-muted)' }}>Select a project to view HSE data</p>
          </div>
        )}

        {/* Tab content */}
        {!loading && projectId && (
          <>
            {activeTab === 'overview' && <OverviewTab stats={stats} triangle={triangle} trend={trend} projectId={projectId} />}
            {activeTab === 'insights' && <ModuleInsightsDashboard projectId={projectId} moduleKey="hse" color="#EF4444" deepLinkBase="/project-controls/hse" />}
            {activeTab === 'incidents' && <IncidentsTab incidents={incidents} total={incidentsTotal} projectId={projectId} onRefresh={fetchAll} />}
            {activeTab === 'permits' && <PermitsTab permits={permits} projectId={projectId} onRefresh={fetchAll} />}
            {activeTab === 'environmental' && <EnvironmentalTab envData={envData} envDashboard={envDashboard} projectId={projectId} onRefresh={fetchAll} />}
            {activeTab === 'inspections' && <InspectionsTab inspections={inspections} projectId={projectId} onRefresh={fetchAll} />}
            {activeTab === 'observations' && <ObservationsTab observations={observations} summary={obsSummary} projectId={projectId} onRefresh={fetchAll} />}
            {activeTab === 'tbt-training' && <TBTTrainingTab tbts={tbts} trainings={trainings} drills={drills} projectId={projectId} onRefresh={fetchAll} />}
          </>
        )}

        {/* Empty state after loading with no data */}
        {!loading && projectId && !stats?.periodId && activeTab === 'overview' && (
          <div className="text-center py-12 rounded-xl" style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }}>
            <Siren className="w-10 h-10 mx-auto mb-3" style={{ color: 'var(--text-disabled)' }} />
            <p className="text-sm mb-2" style={{ color: 'var(--text-muted)' }}>No HSE data found for this project</p>
            <p className="text-xs mb-4" style={{ color: 'var(--text-disabled)' }}>Click "Seed Demo Data" to generate sample HSE records</p>
            <Button variant="outline" size="sm" onClick={handleSeed} disabled={seeding} data-testid="hse-seed-empty-btn">
              {seeding ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <HardHat className="w-3.5 h-3.5 mr-1" />}
              Seed Demo Data
            </Button>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
